/**
 * Studio Proof render context
 * ───────────────────────────
 * Builds the `DocumentRenderContext` the docx renderer needs from the studio's
 * PREVIEW SAMPLE (so Proof shows representative values), and fetches the template's
 * asset slots as ArrayBuffers over the existing asset-content endpoint.
 *
 * Asset/color resolution mirrors deal-time export exactly: a slot with no asset
 * renders BLANK (no workspace fallback), and the brand color defaults to `1E3A5F`
 * - so the Proof preview cannot diverge from what ships (the renderer itself is the
 * same; see the render-parity test in @protoss/doc).
 */

import { markdownToDocumentChildren, type DocumentRenderContext } from '@protoss/doc';
import { getAuthToken } from '../../../../lib/apiClient';
import type { PreviewSample } from '../../../../components/doc';

type AssetMap = Record<string, { buffer: ArrayBuffer; extension: 'png' | 'jpeg' }>;

/** Fetch each asset slot's bytes as an ArrayBuffer (missing/failed slots are skipped). */
export async function fetchAssetBuffers(assetSlots: Record<string, string>): Promise<AssetMap> {
  const out: AssetMap = {};
  const token = await getAuthToken().catch(() => null);
  if (!token) return out;
  await Promise.all(
    Object.entries(assetSlots).map(async ([slot, uuid]) => {
      if (!uuid) return;
      try {
        const res = await fetch(`/api/workspace-assets/${encodeURIComponent(uuid)}/content`, {
          headers: { Authorization: `Bearer ${token}` },
        });
        if (!res.ok) return;
        const buffer = await res.arrayBuffer();
        const type = res.headers.get('content-type') ?? '';
        out[slot] = { buffer, extension: type.includes('png') ? 'png' : 'jpeg' };
      } catch {
        // A failed asset just renders blank (matches export).
      }
    })
  );
  return out;
}

/** Map the preview sample + brand color + assets into a docx render context. */
export function buildStudioRenderContext(
  sample: PreviewSample,
  brandColor: string | null,
  assets: AssetMap
): DocumentRenderContext {
  const context: Record<string, string> = {};
  const date: Record<string, string> = {};
  for (const [key, value] of Object.entries(sample.tokens)) {
    if (key.startsWith('context.')) context[key.slice('context.'.length)] = value;
    else if (key.startsWith('date.')) date[key.slice('date.'.length)] = value;
    else context[key] = value;
  }

  const ctx: DocumentRenderContext = {
    context,
    date,
    brand: { primary_color: brandColor || '1E3A5F' },
    assets,
  };
  // dynamicBlock arrays + contentBlock narratives ride top-level keys by dataKey.
  for (const [dataKey, rows] of Object.entries(sample.data)) ctx[dataKey] = rows;
  for (const [dataKey, md] of Object.entries(sample.narrative)) {
    ctx[dataKey] = markdownToDocumentChildren(md);
  }
  return ctx;
}
